import {
  differenceInYears,
  differenceInMonths,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds,
  differenceInWeeks,
  addYears,
  addMonths,
  format,
  isValid,
  isAfter,
  isBefore,
  parse,
  setYear,
} from "date-fns";
import { gregorianToBangla } from "@/lib/bengaliDate";

export interface AgeBreakdown {
  years: number;
  months: number;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

export interface AgeTotals {
  months: number;
  weeks: number;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

export interface NextBirthday {
  date: Date;
  daysUntil: number;
  /** True when adjusted from Feb 29 → Feb 28 in a non-leap year */
  adjustedForLeap: boolean;
}

export interface BengaliAge {
  age: number;            // Full Bengali years elapsed
  birthYear: number;      // Bangla shon at birth
  currentYear: number;    // Bangla shon at "as of"
}

export interface HijriAge {
  age: number;
  birthYear: number;
  currentYear: number;
}

export type ZodiacKey =
  | "aries" | "taurus" | "gemini" | "cancer" | "leo" | "virgo"
  | "libra" | "scorpio" | "sagittarius" | "capricorn" | "aquarius" | "pisces";

export interface AgeResult {
  breakdown: AgeBreakdown;
  totals: AgeTotals;
  nextBirthday: NextBirthday;
  dayOfWeekBorn: string;       // e.g. "Monday"
  zodiac: ZodiacKey;
}

/* -------- input parsing -------- */

/** Parse a DD/MM/YYYY string (Bengali numerals already normalized to ASCII). */
export function parseDDMMYYYY(input: string): Date | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  const formats = ["dd/MM/yyyy", "d/M/yyyy", "dd-MM-yyyy", "yyyy-MM-dd"];
  for (const fmt of formats) {
    const d = parse(trimmed, fmt, new Date());
    if (isValid(d)) return d;
  }
  return null;
}

export function formatDDMMYYYY(date: Date): string {
  return format(date, "dd/MM/yyyy");
}

/* -------- validation -------- */

export type AgeInputError = "future_dob" | "invalid_dob" | "asof_before_dob" | "out_of_range";

export interface ValidationResult {
  ok: boolean;
  error?: AgeInputError;
}

const MIN_DATE = new Date(1900, 0, 1);

export function validateAgeInputs(dob: Date | null, asOf: Date): ValidationResult {
  if (!dob || !isValid(dob)) return { ok: false, error: "invalid_dob" };
  if (isBefore(dob, MIN_DATE)) return { ok: false, error: "out_of_range" };
  const today = new Date();
  if (isAfter(dob, today)) return { ok: false, error: "future_dob" };
  if (isBefore(asOf, dob)) return { ok: false, error: "asof_before_dob" };
  return { ok: true };
}

/* -------- core calculation -------- */

export function computeAge(dob: Date, asOf: Date): AgeResult {
  // Years / Months / Days breakdown
  const years = differenceInYears(asOf, dob);
  const afterYears = addYears(dob, years);
  const months = differenceInMonths(asOf, afterYears);
  const afterMonths = addMonths(afterYears, months);
  const days = differenceInDays(asOf, afterMonths);

  const hours = differenceInHours(asOf, dob) % 24;
  const minutes = differenceInMinutes(asOf, dob) % 60;
  const seconds = differenceInSeconds(asOf, dob) % 60;

  const totals: AgeTotals = {
    months: differenceInMonths(asOf, dob),
    weeks: differenceInWeeks(asOf, dob),
    days: differenceInDays(asOf, dob),
    hours: differenceInHours(asOf, dob),
    minutes: differenceInMinutes(asOf, dob),
    seconds: differenceInSeconds(asOf, dob),
  };

  return {
    breakdown: { years, months, days, hours, minutes, seconds },
    totals,
    nextBirthday: nextBirthday(dob, asOf),
    dayOfWeekBorn: format(dob, "EEEE"),
    zodiac: zodiacFor(dob),
  };
}

/* -------- next birthday (with Feb 29 handling) -------- */

function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

export function nextBirthday(dob: Date, asOf: Date): NextBirthday {
  const month = dob.getMonth();
  const day = dob.getDate();
  const isFeb29 = month === 1 && day === 29;

  let year = asOf.getFullYear();
  const buildBirthday = (y: number): { date: Date; adjusted: boolean } => {
    if (isFeb29 && !isLeapYear(y)) {
      return { date: new Date(y, 1, 28), adjusted: true };
    }
    return { date: new Date(y, month, day), adjusted: false };
  };

  let { date, adjusted } = buildBirthday(year);
  // Compare just the calendar date (ignore time of day)
  const asOfDay = new Date(asOf.getFullYear(), asOf.getMonth(), asOf.getDate());
  const candidate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  if (candidate < asOfDay) {
    year += 1;
    ({ date, adjusted } = buildBirthday(year));
  }
  const daysUntil = differenceInDays(date, asOfDay);
  return { date, daysUntil, adjustedForLeap: adjusted };
}

/* -------- zodiac -------- */

const ZODIAC_RANGES: { sign: ZodiacKey; from: [number, number] }[] = [
  { sign: "capricorn",   from: [12, 22] },
  { sign: "aquarius",    from: [1, 20] },
  { sign: "pisces",      from: [2, 19] },
  { sign: "aries",       from: [3, 21] },
  { sign: "taurus",      from: [4, 20] },
  { sign: "gemini",      from: [5, 21] },
  { sign: "cancer",      from: [6, 21] },
  { sign: "leo",         from: [7, 23] },
  { sign: "virgo",       from: [8, 23] },
  { sign: "libra",       from: [9, 23] },
  { sign: "scorpio",     from: [10, 23] },
  { sign: "sagittarius", from: [11, 22] },
  { sign: "capricorn",   from: [12, 22] },
];

export function zodiacFor(date: Date): ZodiacKey {
  const m = date.getMonth() + 1;
  const d = date.getDate();
  // Find the latest range that started on/before this date in the year
  let current: ZodiacKey = "capricorn";
  for (const { sign, from } of ZODIAC_RANGES) {
    if (m > from[0] || (m === from[0] && d >= from[1])) {
      current = sign;
    }
  }
  return current;
}

/* -------- Bengali calendar age -------- */

export function bengaliAge(dob: Date, asOf: Date): BengaliAge {
  const birth = gregorianToBangla(dob);
  const now = gregorianToBangla(asOf);
  let age = now.year - birth.year;
  // If the Bengali birthday hasn't occurred yet in the current Bangla year, subtract one
  const passed =
    now.monthIndex > birth.monthIndex ||
    (now.monthIndex === birth.monthIndex && now.day >= birth.day);
  if (!passed) age -= 1;
  return { age: Math.max(age, 0), birthYear: birth.year, currentYear: now.year };
}

/* -------- Hijri age (Umm al-Qura via Intl) -------- */

interface HijriParts {
  year: number;
  monthIndex: number; // 0-11
  day: number;
}

function gregorianToHijri(date: Date): HijriParts {
  const fmt = new Intl.DateTimeFormat("en-u-ca-islamic-umalqura", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  });
  const parts = fmt.formatToParts(date);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "0";
  // The "year" part can include era, e.g. "1447 AH" — strip non-digits
  const year = parseInt(get("year").replace(/\D/g, ""), 10);
  const month = parseInt(get("month"), 10);
  const day = parseInt(get("day"), 10);
  return { year, monthIndex: month - 1, day };
}

export function hijriAge(dob: Date, asOf: Date): HijriAge {
  const birth = gregorianToHijri(dob);
  const now = gregorianToHijri(asOf);
  let age = now.year - birth.year;
  const passed =
    now.monthIndex > birth.monthIndex ||
    (now.monthIndex === birth.monthIndex && now.day >= birth.day);
  if (!passed) age -= 1;
  return { age: Math.max(age, 0), birthYear: birth.year, currentYear: now.year };
}

/* -------- helpers used by formatters -------- */

/** Build the primary "X years, Y months, Z days" sentence. */
export function formatPrimaryAge(
  breakdown: AgeBreakdown,
  lang: "en" | "bn",
  toLocaleNum: (n: number) => string,
): string {
  const { years, months, days } = breakdown;
  if (lang === "bn") {
    return `আপনার বয়স ${toLocaleNum(years)} বছর, ${toLocaleNum(months)} মাস, ${toLocaleNum(days)} দিন`;
  }
  return `You are ${toLocaleNum(years)} year${years === 1 ? "" : "s"}, ${toLocaleNum(months)} month${months === 1 ? "" : "s"}, ${toLocaleNum(days)} day${days === 1 ? "" : "s"} old`;
}

/** Avoid linter unused-import noise — re-exported for convenience */
export { setYear };
